import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axiosInstance from "../helpers/axios";

const AiCreditsContext = createContext(null);

/**
 * Provider that shares a single credit balance fetch across all AI components.
 * Wrap your app (or the relevant subtree) with this provider.
 */
export function AiCreditsProvider({ children }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  const refreshBalance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/ai/credits/balance");
      setBalance(res?.data || null);
    } catch (err) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  const deductCredits = useCallback(
    async ({ action, creditsUsed = 1, targetUserId, targetApplicationId, targetPolicyId, metadata }) => {
      try {
        const res = await axiosInstance.post("/admin/ai/credits/deduct", {
          action,
          creditsUsed,
          targetUserId,
          targetApplicationId,
          targetPolicyId,
          metadata,
        });
        const data = res?.data;
        if (data?.success) {
          refreshBalance();
          return { success: true };
        }
        return { success: false, message: data?.message || "Insufficient credits" };
      } catch (err) {
        return { success: false, message: err?.response?.data?.message || "Failed to deduct credits" };
      }
    },
    [refreshBalance],
  );

  return (
    <AiCreditsContext.Provider value={{ balance, loading, refreshBalance, deductCredits }}>
      {children}
    </AiCreditsContext.Provider>
  );
}

/**
 * Hook to consume the shared AI credits context.
 * Falls back to independent fetch if used outside the provider (backward compatible).
 */
export function useAiCreditsContext() {
  const context = useContext(AiCreditsContext);
  if (!context) {
    // Fallback: not wrapped in provider — shouldn't happen in production
    console.warn("useAiCreditsContext used outside AiCreditsProvider");
    return { balance: null, loading: false, refreshBalance: () => {}, deductCredits: async () => ({ success: false, message: "No provider" }) };
  }
  return context;
}
